import * as realAmplitude from '@amplitude/analytics-browser';
import * as mockAmplitude from '../lib/mockAmplitude';
import { createContext, useContext, useEffect, useMemo, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { CookiePreferences } from '../lib/cookies';

const amplitude =
  window.location.hostname === 'localhost' ||
  window.location.hostname.startsWith('192.168')
    ? mockAmplitude
    : realAmplitude;

const AMPLITUDE_API_KEY = '2032f99eade4ad6d54c694c81ff25c9b';

// eslint-disable-next-line react-refresh/only-export-components
export const AnalyticsContext = createContext<{
  trackEvent: (eventName: string, eventData?: Record<string, any>) => void;
}>({
  trackEvent: () => {
    console.warn('AnalyticsContext not provided!');
  },
});

// eslint-disable-next-line react-refresh/only-export-components
export const useAnalytics = () => useContext(AnalyticsContext);

interface ConsentAwareAnalyticsProviderProps {
  children: React.ReactNode;
  cookiePreferences: CookiePreferences;
}

/**
 * Analytics provider that respects cookie consent and responds to changes
 */
export function ConsentAwareAnalyticsProvider({
  children,
  cookiePreferences,
}: ConsentAwareAnalyticsProviderProps) {
  const location = useLocation();
  const isInitializedRef = useRef(false);
  const previousConsentRef = useRef(cookiePreferences.analytics);

  // Initialize or shutdown Amplitude based on consent
  useEffect(() => {
    const hasConsent = cookiePreferences.analytics;

    if (hasConsent && !isInitializedRef.current) {
      // Initialize Amplitude
      amplitude.init(AMPLITUDE_API_KEY, {
        autocapture: true,
      });
      isInitializedRef.current = true;

      // Track the current page if we just got consent
      const path = location.pathname + location.search;
      amplitude.track('Page Viewed', { path });
    } else if (!hasConsent && isInitializedRef.current) {
      // User revoked consent - shut down Amplitude
      // Note: Amplitude doesn't have a built-in shutdown method,
      // but we can reset the initialization flag and stop tracking
      isInitializedRef.current = false;

      // Clear any Amplitude data if possible
      if ('reset' in amplitude && typeof amplitude.reset === 'function') {
        amplitude.reset();
      }
    }

    previousConsentRef.current = hasConsent;
  }, [cookiePreferences.analytics, location]);

  // Track page views when location changes
  useEffect(() => {
    if (cookiePreferences.analytics && isInitializedRef.current) {
      const path = location.pathname + location.search;
      amplitude.track('Page Viewed', { path });
    }
  }, [location, cookiePreferences.analytics]);

  const value = useMemo(
    () => ({
      trackEvent: (eventName: string, eventData?: Record<string, any>) => {
        if (cookiePreferences.analytics && isInitializedRef.current) {
          amplitude.track(eventName, eventData);
        } else {
          // Log to console in development if no consent
          if (process.env.NODE_ENV === 'development') {
            console.log(
              '[Analytics] Event not tracked (no consent):',
              eventName,
              eventData,
            );
          }
        }
      },
    }),
    [cookiePreferences.analytics],
  );

  return (
    <AnalyticsContext.Provider value={value}>
      {children}
    </AnalyticsContext.Provider>
  );
}
